import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Activity } from '../types/Reports/ShiftEnd/Activity'
import { TimeChanged, TimeSheet } from '../types/Reports/ShiftEnd/TimeSheet'

dayjs.extend(utc)

export function splitTimesheet(newActivity: Activity, currentActivityList: Activity[]): Activity[] {
	const prevRecord =
		currentActivityList
			.filter((r) => new Date(r.startTime) <= newActivity.startTime)
			.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
			.find(() => true) || null

	const nextRecord =
		currentActivityList
			.filter((r) => new Date(r.startTime) > newActivity.startTime)
			.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
			.find(() => true) || null

	if (prevRecord) {
		prevRecord.endTime = newActivity.startTime
	}
	if (nextRecord) {
		newActivity.endTime = nextRecord.startTime
	}
	currentActivityList.push(newActivity)
	return currentActivityList
}
export function mergeConsecutiveDuplicates(currentActivityList: Activity[]): Activity[] {
	const sortedActivityList = currentActivityList.sort(
		(a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
	)

	for (let i = 0; i < sortedActivityList.length - 1; i++) {
		const current = sortedActivityList[i]
		const next = sortedActivityList[i + 1]

		const commentsMatch = (current.comment ?? '') === (next.comment ?? '')

		if (
			commentsMatch &&
			((current.reworkId && next.reworkId && current.reworkId === next.reworkId) ||
				(current.workItemTaskId &&
					next.workItemTaskId &&
					current.workItemTaskId === next.workItemTaskId))
		) {
			if (!next.endTime) {
				current.endTime = null
			} else {
				current.endTime = next.endTime
			}
			sortedActivityList.splice(i + 1, 1)
			i--
		}
	}
	return sortedActivityList
}

export const calculateHours = (startTimeDate: Date, endTimeDate: Date) => {
	const startTime = dayjs(startTimeDate).set('year', 1970).set('month', 0).set('date', 1)
	let endTime = dayjs(endTimeDate).set('year', 1970).set('month', 0).set('date', 1)
	if (endTime.isBefore(startTime)) {
		endTime = endTime.add(1, 'day')
	}
	const timeVal = dayjs(endTime).diff(dayjs(startTime), 'second')
	// Return exact decimal value without rounding
	return timeVal / 3600
}

const SECONDS_PER_DAY = 24 * 60 * 60
const HALF_DAY_SECONDS = 12 * 60 * 60

/**
 * Normalizes a time diff (in seconds) when the activity spans midnight (e.g. 10pm–2am).
 * Picker may give "today" 2am while stored end is "tomorrow" 2am, yielding a negative diff.
 * Converts that to the intended small adjustment so cascade logic gets a correct value.
 */
export const normalizeTimeDiffForOvernight = (diffSeconds: number): number => {
	if (diffSeconds <= -HALF_DAY_SECONDS) {
		return diffSeconds + SECONDS_PER_DAY
	}
	if (diffSeconds > HALF_DAY_SECONDS) {
		return diffSeconds - SECONDS_PER_DAY
	}
	return diffSeconds
}

/** Seconds from midnight for a given date (time-of-day only). */
export const timeOfDaySeconds = (d: Date): number => {
	const x = dayjs(d)
	return x.hour() * 3600 + x.minute() * 60 + x.second()
}

/** True when the activity crosses midnight (e.g. 10pm to 2am): end time-of-day is before start time-of-day. */
export const activitySpansMidnight = (startTime: Date, endTime: Date | null): boolean => {
	if (!endTime) return false
	return timeOfDaySeconds(endTime) < timeOfDaySeconds(startTime)
}

/**
 * Normalizes activity start/end dates: first activity uses shiftDate; when an activity's end
 * time goes overnight, the next activity's date is increased by one day. Mutates activities in place.
 */
export const normalizeActivityDates = (activities: Activity[], shiftDate: Date): void => {
	const sortedByStart = [...activities].sort(
		(a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
	)
	const setDateKeepTime = (d: Date, day: dayjs.Dayjs) =>
		dayjs(d)
			.year(day.year())
			.month(day.month())
			.date(day.date())
			.toDate()
	let currentDay = dayjs(shiftDate).startOf('day')
	for (const act of sortedByStart) {
		act.startTime = setDateKeepTime(act.startTime, currentDay)
		if (act.endTime && dayjs(act.endTime).isValid()) {
			const spansMidnight = activitySpansMidnight(act.startTime, act.endTime)
			if (spansMidnight) {
				act.endTime = setDateKeepTime(act.endTime, currentDay.add(1, 'day'))
				currentDay = currentDay.add(1, 'day')
			} else {
				act.endTime = setDateKeepTime(act.endTime, currentDay)
			}
		}
	}
}

export const timeCascadeActivities = (
	allActivities: Activity[],
	timeDiff: number,
	timeChangeType: TimeChanged,
	updatedActivityIndex: number,
) => {
	const allActivitiesClone = structuredClone(allActivities)
	if (timeChangeType === TimeChanged.StartTimeChanged) {
		allActivitiesClone[updatedActivityIndex].startTime = dayjs(
			allActivitiesClone[updatedActivityIndex].startTime,
		)
			.add(timeDiff, 'second')
			.toDate()
		if (
			dayjs(allActivitiesClone[updatedActivityIndex].startTime).isAfter(
				allActivitiesClone[updatedActivityIndex].endTime,
			)
		) {
			allActivitiesClone[updatedActivityIndex].endTime = dayjs(
				allActivitiesClone[updatedActivityIndex].endTime,
			)
				.add(timeDiff, 'second')
				.toDate()
			forwardPropogateTime(allActivitiesClone, updatedActivityIndex, timeDiff)
		}
		backwardPropogateTime(allActivitiesClone, updatedActivityIndex, timeDiff)
	} else {
		allActivitiesClone[updatedActivityIndex].endTime = dayjs(
			allActivitiesClone[updatedActivityIndex].endTime,
		)
			.add(timeDiff, 'second')
			.toDate()

		// backward propogate time if new endTime is before current startTime
		if (
			dayjs(allActivitiesClone[updatedActivityIndex].endTime).isBefore(
				allActivitiesClone[updatedActivityIndex].startTime,
			)
		) {
			allActivitiesClone[updatedActivityIndex].startTime = dayjs(
				allActivitiesClone[updatedActivityIndex].startTime,
			)
				.add(timeDiff, 'second')
				.toDate()
			backwardPropogateTime(allActivitiesClone, updatedActivityIndex, timeDiff)
		}

		forwardPropogateTime(allActivitiesClone, updatedActivityIndex, timeDiff)
	}

	return allActivitiesClone
}

const forwardPropogateTime = (
	allActivitiesClone: Activity[],
	updatedActivityIndex: number,
	timeDiff: number,
) => {
	for (let i = updatedActivityIndex + 1; i < allActivitiesClone.length; i++) {
		allActivitiesClone[i].endTime = dayjs(allActivitiesClone[i].endTime)
			.add(timeDiff, 'second')
			.toDate()
		allActivitiesClone[i].startTime = dayjs(allActivitiesClone[i].startTime)
			.add(timeDiff, 'second')
			.toDate()
	}
}

const backwardPropogateTime = (
	allActivitiesClone: Activity[],
	updatedActivityIndex: number,
	timeDiff: number,
) => {
	for (let i = updatedActivityIndex - 1; i >= 0; i--) {
		allActivitiesClone[i].endTime = dayjs(allActivitiesClone[i].endTime)
			.add(timeDiff, 'second')
			.toDate()
		allActivitiesClone[i].startTime = dayjs(allActivitiesClone[i].startTime)
			.add(timeDiff, 'second')
			.toDate()
	}
}

export const findStartEndTime = (timesheet: TimeSheet) => {
	if (timesheet.activities.length === 0) {
		return {
			minStartTime: undefined,
			maxEndTime: undefined,
		}
	}
	let minStartTime = dayjs(timesheet.activities[0].startTime).toDate()
	let maxEndTime = dayjs(timesheet.activities[0].endTime).toDate()

	timesheet.activities.map((activity: Activity) => {
		if (dayjs(activity.startTime).isBefore(minStartTime)) {
			minStartTime = dayjs(activity.startTime).toDate()
		}
		if (dayjs(activity.endTime).isAfter(maxEndTime)) {
			maxEndTime = dayjs(activity.endTime).toDate()
		}
	})
	return {
		minStartTime: dayjs(minStartTime).toDate(),
		maxEndTime: dayjs(maxEndTime).toDate(),
	}
}

export const workSheetTotalHoursCalc = (activities: Activity[]) => {
	return activities.reduce(
		(previousActivity: number, currentActivity: Activity) =>
			previousActivity +
			(currentActivity.endTime
				? calculateHours(currentActivity.startTime, currentActivity.endTime)
				: 0),
		0,
	)
}

/** True if timesheet title, times, or any activity/comment match the search string (case-insensitive). */
export const matchesSearch = (searchLower: string, timeSheet: TimeSheet): boolean =>
	(timeSheet.title?.toLowerCase().includes(searchLower) ?? false) ||
	(timeSheet.startTime
		? dayjs(timeSheet.startTime).format('h:mm A').toLowerCase().includes(searchLower)
		: false) ||
	(timeSheet.endTime
		? dayjs(timeSheet.endTime).format('h:mm A').toLowerCase().includes(searchLower)
		: false) ||
	timeSheet.activities.some(
		(t) =>
			t.activity.toLowerCase().includes(searchLower) ||
			t.comment.toLowerCase().includes(searchLower) ||
			(t.startTime != null
				? dayjs(t.startTime).format('h:mm A').toLowerCase().includes(searchLower)
				: false) ||
			(t.endTime != null
				? dayjs(t.endTime).format('h:mm A').toLowerCase().includes(searchLower)
				: false),
	)

/** True if activity label, comment, or times match the search string (case-insensitive). */
export const activityMatchesSearch = (searchLower: string, activity: Activity): boolean =>
	activity.activity.toLowerCase().includes(searchLower) ||
	activity.comment.toLowerCase().includes(searchLower) ||
	(activity.startTime != null
		? dayjs(activity.startTime).format('h:mm A').toLowerCase().includes(searchLower)
		: false) ||
	(activity.endTime != null
		? dayjs(activity.endTime).format('h:mm A').toLowerCase().includes(searchLower)
		: false)

/** Maps API timesheet response to UI shape with UTC normalization and derived hours. */
export const mapTimeSheetsResponse = (response: TimeSheet[]): TimeSheet[] => {
	return response.map((item: TimeSheet) => {
		const { minStartTime, maxEndTime } = findStartEndTime(item)
		return {
			id: item.id,
			title: item.title,
			workerId: item.workerId,
			signInAt: item.signInAt
				? dayjs(item.signInAt).utc(true).set('second', 1).set('millisecond', 0).toDate()
				: undefined,
			signOutAt: item.signOutAt
				? dayjs(item.signOutAt).utc(true).set('second', 1).set('millisecond', 0).toDate()
				: undefined,
			startTime: minStartTime
				? dayjs(minStartTime).utc(true).set('second', 1).set('millisecond', 0).toDate()
				: undefined,
			endTime: maxEndTime
				? dayjs(maxEndTime).utc(true).set('second', 1).set('millisecond', 0).toDate()
				: undefined,
			hours: Number(workSheetTotalHoursCalc(item.activities).toFixed(2)),
			activities: item.activities.map((activity: Activity) => ({
				id: activity.id,
				type: activity.type,
				activity: activity.activity,
				startTime: dayjs(activity.startTime)
					.utc(true)
					.set('second', 1)
					.set('millisecond', 0)
					.toDate(),
				endTime: dayjs(activity.endTime)
					.utc(true)
					.set('second', 1)
					.set('millisecond', 0)
					.toDate(),
				comment: activity.comment,
				hours: activity.endTime
					? Number(calculateHours(activity.startTime, activity.endTime).toFixed(2))
					: null,
				workItemId: activity.workItemId,
				workItemTaskId: activity.workItemTaskId,
				reworkId: activity.reworkId,
			})),
			confirmed: item.confirmed,
		}
	})
}
